import type { PageNode } from "./pages";
import type {
  PageComponentDefinition,
  PageComponentProp,
  PageComponentPropType,
  PageComponentValue,
  PageComponentValues,
} from "./page-components";

// The runtime half of components (see page-components.ts): their paths and how an instance draws
// its component's elements, with no schema library, so a creator site's script stays small.

/** Element kinds a component may hold: layout, words, pictures, links and videos. */
export const componentElementKinds = [
  "frame",
  "text",
  "image",
  "link",
  "video",
  "tabs",
  "accordion",
] as const;

const componentPathPrefix = "/:components/";
const isComponentKey = (key: string) => key.length <= 60 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(key);

export function componentPagePath(key: string) {
  return `${componentPathPrefix}${key}`;
}

/** Whether a page document is a component rather than a page visitors open. */
export function isComponentPath(path: string) {
  return (
    path.startsWith(componentPathPrefix) && isComponentKey(path.slice(componentPathPrefix.length))
  );
}

/** A component's key from its name: "Pricing card" is pricing-card. */
export function componentKey(name: string) {
  return (
    name
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60)
      .replace(/-+$/, "") || "component"
  );
}

/** A property's value on an instance, or its default when the instance leaves it unset. */
export function componentValue(
  prop: PageComponentProp,
  values: Readonly<PageComponentValues>,
): PageComponentValue {
  const value = values[prop.key];
  if (prop.type === "toggle") return typeof value === "boolean" ? value : prop.default;
  if (prop.type === "link")
    return value && typeof value === "object" && "href" in value ? value : prop.default;
  return typeof value === "string" ? value : prop.default;
}

/**
 * A component's elements as one instance draws them: the properties it sets filled in (the rest
 * keep the component's own content), elements behind a switched-off property left out, and every
 * ID scoped to the instance so a page can hold several. Values stay plain text and addresses; the
 * page's own rules check links and pictures.
 */
export function componentInstanceNodes(
  definition: { nodes: readonly PageNode[]; component: PageComponentDefinition },
  instance: { id: string; props: Readonly<PageComponentValues> },
): PageNode[] {
  const props = new Map(definition.component.props.map((prop) => [prop.key, prop]));
  const read = (key: string | undefined, type: PageComponentPropType) => {
    const prop = key ? props.get(key) : undefined;
    if (prop?.type !== type) return undefined;
    // A switch the instance leaves alone keeps its default; other properties keep the content.
    return prop.type === "toggle" || instance.props[prop.key] !== undefined
      ? componentValue(prop, instance.props)
      : undefined;
  };
  const expand = (nodes: readonly PageNode[]): PageNode[] =>
    nodes.flatMap((node): PageNode[] => {
      if (node.showProp && read(node.showProp, "toggle") === false) return [];
      const id = `${instance.id}__${node.id}`.slice(0, 200);
      const {
        prop: _prop,
        showProp: _showProp,
        ...rest
      } = node as PageNode & {
        prop?: string;
        showProp?: string;
      };
      if (node.kind === "text") {
        const text = read(node.prop, "text");
        return [{ ...(rest as typeof node), id, ...(typeof text === "string" ? { text } : {}) }];
      }
      if (node.kind === "image") {
        const src = read(node.prop, "image");
        return [{ ...(rest as typeof node), id, ...(typeof src === "string" ? { src } : {}) }];
      }
      if (node.kind === "link") {
        const link = read(node.prop, "link");
        return [
          {
            ...(rest as typeof node),
            id,
            ...(link && typeof link === "object" ? { label: link.label, href: link.href } : {}),
            // A property's link is an address, not one of the creator's pages by ID.
            ...(link && typeof link === "object" ? { pageId: undefined } : {}),
          },
        ];
      }
      if (node.kind === "tabs" || node.kind === "accordion") {
        const children = expand(node.children);
        if (!children.length) return [];
        const initialId = node.children[node.initialIndex]?.id;
        const initialIndex =
          initialId === undefined
            ? -1
            : children.findIndex(
                (panel) => panel.id === `${instance.id}__${initialId}`.slice(0, 200),
              );
        return [
          {
            ...(rest as typeof node),
            id,
            children,
            initialIndex: initialIndex < 0 && node.kind === "tabs" ? 0 : initialIndex,
          },
        ];
      }
      if (node.kind === "frame")
        return [{ ...(rest as typeof node), id, children: expand(node.children) }];
      return [{ ...(rest as PageNode), id }];
    });
  return expand(definition.nodes);
}

/** What a property shows when an instance leaves it alone: its element's own content. */
export function componentContent(
  definition: { nodes: readonly PageNode[]; component: PageComponentDefinition },
  prop: PageComponentProp,
): PageComponentValue {
  const walk = (nodes: readonly PageNode[]): PageNode | undefined => {
    for (const node of nodes) {
      if ("prop" in node && node.prop === prop.key) return node;
      const found =
        node.kind === "frame" || node.kind === "tabs" || node.kind === "accordion"
          ? walk(node.children)
          : undefined;
      if (found) return found;
    }
    return undefined;
  };
  const node = prop.type === "toggle" ? undefined : walk(definition.nodes);
  if (node?.kind === "text") return node.text;
  if (node?.kind === "image") return node.src;
  if (node?.kind === "link") return { label: node.label, href: node.href };
  return prop.default;
}

/** A component with each property's default as its elements show it, as it is saved. */
export function withComponentDefaults<
  Document extends { nodes: PageNode[]; component?: PageComponentDefinition },
>(document: Document): Document {
  const component = document.component;
  if (!component) return document;
  return {
    ...document,
    component: {
      ...component,
      props: component.props.map(
        (prop) =>
          ({
            ...prop,
            default: componentContent({ nodes: document.nodes, component }, prop),
          }) as PageComponentProp,
      ),
    },
  };
}
